import { c as _c } from "react/compiler-runtime";
import type { DataIdentifier, Entity } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import { getCustomDIs } from '@/entities/utils/get-dis';
import type { EntityVault } from '@/types/vault';
import hasLegalStatus from '../../utils/has-legal-status';
import hasNationality from '../../utils/has-nationality';
export type FieldsetField = {
  di: DataIdentifier;
}; // TODO: add customRender property to FieldsetField
export type Fieldset = Record<string, {
  fields: FieldsetField[];
  title: string;
}>;
const useFieldsets = (entity, vaultData) => {
  const $ = _c(19);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "pages.user.vault"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t: userT
  } = useTranslation("common", t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "vault"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    t: businessT
  } = useTranslation("business-details", t1);
  let t2;
  let t3;
  if ($[2] !== businessT || $[3] !== entity.data || $[4] !== entity.kind || $[5] !== userT || $[6] !== vaultData) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const customDIs = getCustomDIs(vaultData || {}, entity.data);
      const includeNationality = entity.kind === "person" && hasNationality(vaultData) && !hasLegalStatus(vaultData);
      if (entity.kind === "person") {
        t3 = {
          basic: {
            title: userT(_temp),
            fields: [{
              di: "id.first_name"
            }, {
              di: "id.middle_name"
            }, {
              di: "id.last_name"
            }, {
              di: "id.email"
            }, {
              di: "id.phone_number"
            }]
          },
          address: {
            title: userT(_temp2),
            fields: [{
              di: "id.country"
            }, {
              di: "id.address_line1"
            }, {
              di: "id.address_line2"
            }, {
              di: "id.city"
            }, {
              di: "id.zip"
            }, {
              di: "id.state"
            }]
          },
          identity: {
            title: userT(_temp3),
            fields: [{
              di: "id.ssn9"
            }, {
              di: "id.ssn4"
            }, {
              di: "id.dob"
            }, ...(includeNationality ? [{
              di: "id.nationality" as DataIdentifier
            }] : [])]
          },
          ...(hasLegalStatus(vaultData) && {
            usLegalStatus: {
              title: userT(_temp4),
              fields: [{
                di: "id.us_legal_status"
              }, {
                di: "id.nationality"
              }, {
                di: "id.citizenships"
              }, {
                di: "id.visa_kind"
              }, {
                di: "id.visa_expiration_date"
              }]
            }
          }),
          ...(customDIs.length > 0 && {
            custom: {
              title: userT(_temp5),
              fields: customDIs.map(_temp6)
            }
          })
        };
        break bb0;
      }
      let t4;
      if ($[9] !== businessT) {
        t4 = businessT(_temp7);
        $[9] = businessT;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      let t5;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = [{
          di: "business.name"
        }, {
          di: "business.dba"
        }, {
          di: "business.tin"
        }, {
          di: "business.website"
        }, {
          di: "business.phone_number"
        }, {
          di: "business.formation_state"
        }, {
          di: "business.formation_date"
        }];
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      let t6;
      if ($[12] !== t4) {
        t6 = {
          title: t4,
          fields: t5
        };
        $[12] = t4;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      let t7;
      if ($[14] !== businessT) {
        t7 = businessT(_temp8);
        $[14] = businessT;
        $[15] = t7;
      } else {
        t7 = $[15];
      }
      let t8;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = [{
          di: "business.country"
        }, {
          di: "business.address_line1"
        }, {
          di: "business.address_line2"
        }, {
          di: "business.city"
        }, {
          di: "business.state"
        }, {
          di: "business.zip"
        }];
        $[16] = t8;
      } else {
        t8 = $[16];
      }
      let t9;
      if ($[17] !== t7) {
        t9 = {
          title: t7,
          fields: t8
        };
        $[17] = t7;
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      t2 = {
        basic: t6,
        address: t9,
        ...(customDIs.length > 0 && {
          custom: {
            title: businessT(_temp9),
            fields: customDIs.map(_temp10)
          }
        })
      };
    }
    $[2] = businessT;
    $[3] = entity.data;
    $[4] = entity.kind;
    $[5] = userT;
    $[6] = vaultData;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  return t2;
};
export default useFieldsets;
function _temp($) {
  return $.basic.title;
}
function _temp2($_0) {
  return $_0.address.title;
}
function _temp3($_1) {
  return $_1.identity.title;
}
function _temp4($_2) {
  return $_2.usLegalStatus.title;
}
function _temp5($_3) {
  return $_3.custom.title;
}
function _temp6(di) {
  return {
    di
  } as FieldsetField;
}
function _temp7($_4) {
  return $_4.basic.title;
}
function _temp8($_5) {
  return $_5.address.title;
}
function _temp9($_6) {
  return $_6.custom.title;
}
function _temp10(di_0) {
  return {
    di: di_0
  } as FieldsetField;
}